/**
 * Base types and classes for API clients
 * This file contains only types and abstract classes without any implementation
 * that could cause side effects or import issues.
 */
/**
 * Error thrown by service methods that wrap an HTTP call when the response
 * is non-2xx. Carries the status code so callers (notably the mobile
 * background upload queue) can decide whether to retry. Before this existed
 * we threw plain Error and the queue retried every failure 3 times — which
 * meant a permanent 4xx like "work order is closed" still burned through
 * MAX_RETRIES before being marked failed.
 */
export class HttpError extends Error {
    constructor(status, message, body) {
        super(message);
        this.name = 'HttpError';
        this.status = status;
        this.body = body;
        // Restore prototype chain so `instanceof HttpError` works after transpile.
        Object.setPrototypeOf(this, HttpError.prototype);
    }
    /**
     * True for 4xx statuses where retrying cannot succeed: bad input, the
     * resource is gone, a conflict in resource state, etc. Auth (401),
     * timeout (408), rate-limit (429) are explicitly NOT in this set —
     * those can transiently succeed on retry (token refresh, backoff).
     */
    isPermanent4xx() {
        if (this.status < 400 || this.status >= 500)
            return false;
        return this.status !== 401 && this.status !== 408 && this.status !== 429;
    }
}
/**
 * Base API client class with common functionality
 */
export class BaseApiClient {
    constructor(baseURL) {
        this.baseURL = baseURL;
    }
    getBaseUrl() {
        return this.baseURL;
    }
    setBaseUrl(url) {
        this.baseURL = url;
    }
    /**
     * Make HTTP request - should be overridden by subclasses
     */
    async makeRequest(endpoint, options) {
        throw new Error('makeRequest must be implemented by subclass');
    }
    /**
     * GET request
     */
    async get(endpoint, params) {
        let url = endpoint;
        if (params && Object.keys(params).length > 0) {
            const queryString = this.buildQueryString(params);
            url = `${endpoint}${endpoint.includes('?') ? '&' : '?'}${queryString}`;
        }
        return this.makeRequest(url, { method: 'GET' });
    }
    /**
     * POST request
     */
    async post(endpoint, data) {
        return this.makeRequest(endpoint, {
            method: 'POST',
            body: JSON.stringify(data),
        });
    }
    /**
     * PUT request
     */
    async put(endpoint, data) {
        return this.makeRequest(endpoint, {
            method: 'PUT',
            body: JSON.stringify(data),
        });
    }
    /**
     * PATCH request
     */
    async patch(endpoint, data) {
        return this.makeRequest(endpoint, {
            method: 'PATCH',
            body: JSON.stringify(data),
        });
    }
    /**
     * DELETE request
     */
    async delete(endpoint) {
        return this.makeRequest(endpoint, { method: 'DELETE' });
    }
    /**
     * A request with extra headers — conditional writes (`If-Match`) and the
     * like. The plain verbs above take no headers, and widening each of their
     * signatures would touch every subclass; this is the one place a caller can
     * ask for them. Subclasses route it through their own auth/retry pipeline.
     */
    async send(method, endpoint, data, headers) {
        return this.makeRequest(endpoint, {
            method,
            body: data === undefined ? undefined : JSON.stringify(data),
            headers: headers ?? {},
        });
    }
    /**
     * DELETE request with a JSON body (some endpoints, e.g. bulk tag removal,
     * parse the payload from the request body rather than the query string).
     */
    async deleteWithBody(endpoint, data) {
        return this.makeRequest(endpoint, {
            method: 'DELETE',
            body: JSON.stringify(data),
        });
    }
    /**
     * POST request with FormData
     */
    async postFormData(endpoint, formData) {
        return this.makeRequest(endpoint, {
            method: 'POST',
            body: formData,
        });
    }
    /**
     * PUT request with FormData
     */
    async putFormData(endpoint, formData) {
        return this.makeRequest(endpoint, {
            method: 'PUT',
            body: formData,
        });
    }
    /**
     * Helper to build query string from params object
     */
    buildQueryString(params) {
        const searchParams = new URLSearchParams();
        Object.entries(params).forEach(([key, value]) => {
            if (value !== undefined && value !== null) {
                if (Array.isArray(value)) {
                    value.forEach((v) => searchParams.append(key, String(v)));
                }
                else {
                    searchParams.append(key, String(value));
                }
            }
        });
        return searchParams.toString();
    }
}
